import { Dash } from './dash'
import { Invaders } from './invaders'
import { Snake } from './snake'
import type { Cell, Dir, Game, GameEvent, GameInput } from './types'

export type GameKind = 'snake' | 'invaders' | 'dash'
export type GameMode = 'demo' | 'play'

export interface RunnerStatus {
	kind: GameKind
	mode: GameMode
	score: number
	lives?: number
	wave?: number
	over: boolean
}

export interface RunnerHooks {
	/** A frame to draw. Demos go on the voxel board; play mode goes on the CRT. */
	frame: (cells: Cell[], width: number, height: number, mode: GameMode) => void
	status: (s: RunnerStatus) => void
	event: (e: GameEvent) => void
	/** Fired once when a game ends (game over, demo timeout, or quit). */
	ended: (s: RunnerStatus, reason: 'over' | 'timeout' | 'quit') => void
}

const KEY_DIRS: Record<string, Dir> = {
	ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right',
	w: 'up', s: 'down', a: 'left', d: 'right', W: 'up', S: 'down', A: 'left', D: 'right',
}

/** Owns the tick loop, input state and the current game. One instance per page. */
export class GameRunner {
	private game: Game | null = null
	private mode: GameMode = 'demo'
	private timer = 0
	private startedAt = 0
	private maxMs = 0
	private holdUntil = 0
	private input: GameInput = {}
	private held = new Set<Dir>()
	private touchStart: [number, number] | null = null

	constructor(private hooks: RunnerHooks) {
		addEventListener('pointerdown', this.onPointerDown)
		addEventListener('pointerup', this.onPointerUp)
	}

	get active(): boolean {
		return this.game !== null
	}

	get status(): RunnerStatus | null {
		return this.game ? this.snapshot() : null
	}

	get current(): Game | null {
		return this.game
	}

	start(kind: GameKind, mode: GameMode, maxMs = 0): void {
		this.stop()
		const g = kind === 'snake' ? new Snake() : kind === 'invaders' ? new Invaders() : new Dash()
		this.game = g
		this.mode = mode
		this.maxMs = maxMs
		this.startedAt = performance.now()
		this.holdUntil = 0
		this.input = {}
		this.held.clear()
		this.hooks.status(this.snapshot())
		// the opening board goes out straight away so a screen that is still warming up has something to show
		this.hooks.frame(g.cells(), g.W, g.H, mode)
		this.loop()
	}

	/** Freeze the board for a moment (the CRT is melting in); ticks resume afterwards. */
	hold(ms: number): void {
		if (!this.game || ms <= 0) return
		// the loop itself waits this out, so calling this mid-start doesn't double up the timer
		this.holdUntil = performance.now() + ms
		this.startedAt += ms
	}

	stop(): void {
		clearTimeout(this.timer)
		this.timer = 0
		this.game = null
	}

	quit(): void {
		if (!this.game) return
		const s = this.snapshot()
		this.stop()
		this.hooks.ended(s, 'quit')
	}

	/** The person grabbed the controls during a demo. */
	takeControl(): void {
		const g = this.game
		if (!g || this.mode === 'play') return
		this.mode = 'play'
		this.hooks.status(this.snapshot())
		this.hooks.frame(g.cells(), g.W, g.H, 'play')
	}

	/** Returns true when the key was consumed by the game. */
	keyDown(e: KeyboardEvent): boolean {
		if (!this.game) return false
		if (e.key === 'Escape') { this.quit(); return true }
		const dir = KEY_DIRS[e.key]
		if (dir) {
			this.takeControl()
			this.held.add(dir)
			this.input.held = dir
			this.input.pressed = dir
			return true
		}
		if (e.key === ' ' || e.key === 'Enter') {
			this.takeControl()
			this.input.fire = true
			return true
		}
		return false
	}

	keyUp(e: KeyboardEvent): void {
		const dir = KEY_DIRS[e.key]
		if (!dir) return
		this.held.delete(dir)
		if (this.input.held === dir) this.input.held = [...this.held].pop()
	}

	swipe(dir: Dir): void {
		if (!this.game) return
		this.takeControl()
		this.input.pressed = dir
		this.input.held = dir
		// a swipe on invaders nudges for a few ticks then stops
		setTimeout(() => { if (this.input.held === dir) this.input.held = undefined }, 250)
	}

	tap(): void {
		if (!this.game) return
		this.takeControl()
		this.input.fire = true
	}

	dispose(): void {
		this.stop()
		removeEventListener('pointerdown', this.onPointerDown)
		removeEventListener('pointerup', this.onPointerUp)
	}

	// ---- internals ----

	private snapshot(): RunnerStatus {
		const g = this.game!
		return { kind: g.name, mode: this.mode, score: g.score, lives: g.lives, wave: g.wave, over: g.over }
	}

	private loop = (): void => {
		const g = this.game
		if (!g) return
		const wait = this.holdUntil - performance.now()
		if (wait > 0) { this.timer = window.setTimeout(this.loop, wait); return }
		const events = g.step(this.input, this.mode === 'demo')
		this.input.pressed = undefined
		this.input.fire = false
		this.hooks.frame(g.cells(), g.W, g.H, this.mode)
		for (const e of events) this.hooks.event(e)
		const s = this.snapshot()
		this.hooks.status(s)
		if (g.over) {
			this.stop()
			this.hooks.ended(s, 'over')
			return
		}
		if (this.maxMs && this.mode === 'demo' && performance.now() - this.startedAt > this.maxMs) {
			this.stop()
			this.hooks.ended(s, 'timeout')
			return
		}
		this.timer = window.setTimeout(this.loop, g.tickMs)
	}

	private onPointerDown = (e: PointerEvent): void => {
		// taps and swipes count on the voxel board or on the CRT glass, not on buttons
		if (!this.game || !(e.target as Element | null)?.closest?.('#gl, .crt')) return
		this.touchStart = [e.clientX, e.clientY]
	}

	private onPointerUp = (e: PointerEvent): void => {
		if (!this.touchStart) return
		const [sx, sy] = this.touchStart
		this.touchStart = null
		const dx = e.clientX - sx
		const dy = e.clientY - sy
		if (Math.hypot(dx, dy) < 24) { this.tap(); return }
		this.swipe(Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? 'right' : 'left') : dy > 0 ? 'down' : 'up')
	}
}
